import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCustomerBrandHotelSelection } from 'components/customerBrandHotelSelect/useCustomerBrandHotelSelection';
import { getItem } from 'helpers/storage';
import NonIdealState from 'lqa-ui/lib/Components/NonIdealState';
import LoadingContent from 'lqa-ui/lib/Components/LoadingContent';

import { Analytics } from '../common/utils/analytics';
import { fetchFeatureFlagsFromAPI } from '../helpers/featureFlags/featureFlagsInitializer';
import { getFlag } from '../helpers/featureFlags/featureFlags';

interface OverlaySavedState {
    bodyOverflow: string;
    documentOverflow: string;
    // Pre-overlay window scrollY captured *before* we scroll the page to
    // the top for overlay positioning, so we can return the user to
    // exactly where they were when the drawer closes.
    scrollY: number;
}

// Pixel offset from the top of the viewport that the overlay iframe
// should honour — captured from the iframe container's natural position
// when overlay mode starts, so any sticky top chrome (lqa-app nav) stays
// visible above the drawer instead of getting hidden under it.

const BASE_OVERLAY_IFRAME_STYLE: React.CSSProperties = {
    position: 'fixed',
    left: 0,
    width: '100%',
    border: 'none',
    display: 'block',
    zIndex: 9999,
};

export function MicroApp({ url, requiredFlag }: Readonly<MicroAppProps>) {
    const { t } = useTranslation();
    const userData = getItem('user');
    const containerRef = useRef<HTMLDivElement>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [iframeHeight, setIframeHeight] = useState(500);
    const [minHeight, setMinHeight] = useState(0);
    const [isForbidden, setIsForbidden] = useState(false);
    const [accessState, setAccessState] = useState<'checking' | 'granted' | 'denied'>(
        requiredFlag ? 'checking' : 'granted'
    );

    // Overlay-mode coordination — when the iframe opens a full-viewport
    // drawer/modal, it posts `{type:'overlay-mode', open:true}` and we
    // pin the iframe with `position: fixed; inset: 0` so it physically
    // covers the parent viewport. That way `position: fixed` content
    // inside the iframe (the drawer) is anchored to the iframe's
    // coordinate origin, which now equals the user's visible viewport
    // top-left. No parent-scroll gymnastics required — works the same
    // whether the lqa-app shell scrolls on `window` or on an inner
    // overflow container. We also lock body/html overflow so the
    // (now-hidden) underlying page can't scroll and ignore the iframe's
    // own auto-resize messages until overlay-mode is closed.
    const [isOverlayActive, setIsOverlayActive] = useState(false);
    const [overlayTopOffset, setOverlayTopOffset] = useState(0);
    const overlayActiveRef = useRef(false);
    const overlaySavedStateRef = useRef<OverlaySavedState | null>(null);
    // Pending parent-scroll restore that should fire once `isOverlayActive`
    // flips back to false. Doing the restore inside an effect — rather than
    // synchronously inside `exitOverlayMode` — guarantees React has already
    // committed the inline-iframe DOM, so when we `scrollTo` the document
    // is full-height and the browser doesn't clamp.
    const pendingScrollRestoreRef = useRef<number | null>(null);
    // Tracks an in-flight rAF id for the scroll-restore so we can cancel
    // it if the component unmounts mid-restore.
    const restoreFrameRef = useRef<number | null>(null);
    const iframeOrigin = useMemo(() => new URL(url).origin, [url]);

    useEffect(() => {
        // Calculate the available viewport height
        const calculateMinHeight = () => {
            if (containerRef.current) {
                const containerRect = containerRef.current.getBoundingClientRect();
                const availableHeight = window.innerHeight - containerRect.top - 20;
                setMinHeight(Math.max(500, availableHeight));
            }
        };

        calculateMinHeight();

        window.addEventListener('resize', calculateMinHeight);

        return () => window.removeEventListener('resize', calculateMinHeight);
    }, [containerRef.current, window.innerHeight]);

    const exitOverlayMode = useCallback(() => {
        if (!overlayActiveRef.current) {
            return;
        }
        overlayActiveRef.current = false;
        setIsOverlayActive(false);

        const saved = overlaySavedStateRef.current;
        overlaySavedStateRef.current = null;
        if (saved) {
            document.body.style.overflow = saved.bodyOverflow;
            document.documentElement.style.overflow = saved.documentOverflow;
            // Stash the target scroll position; the effect keyed on
            // `isOverlayActive` will fire it after React commits the inline
            // iframe DOM and the browser has had a frame to relayout.
            pendingScrollRestoreRef.current = saved.scrollY;
        }
    }, []);

    // Restore the parent's scroll position once overlay-mode is fully off.
    // Runs after React commits, then defers two animation frames so the
    // iframe has been re-rendered inline *and* the body has reflowed back
    // to its full pre-overlay height. Without the deferral, `scrollTo`
    // sees a still-short document and clamps the target.
    useEffect(() => {
        if (isOverlayActive) {
            return;
        }
        if (pendingScrollRestoreRef.current === null) {
            return;
        }
        const target = pendingScrollRestoreRef.current;
        pendingScrollRestoreRef.current = null;
        restoreFrameRef.current = requestAnimationFrame(() => {
            restoreFrameRef.current = requestAnimationFrame(() => {
                window.scrollTo(0, target);
                restoreFrameRef.current = null;
            });
        });
        return () => {
            if (restoreFrameRef.current !== null) {
                cancelAnimationFrame(restoreFrameRef.current);
                restoreFrameRef.current = null;
            }
        };
    }, [isOverlayActive]);

    const enterOverlayMode = useCallback(() => {
        if (overlayActiveRef.current) {
            return;
        }
        overlayActiveRef.current = true;

        // Capture pre-overlay state — including the page scroll — so we can
        // restore everything on exit. Save scrollY *before* we scroll to
        // top below, otherwise we'd persist the post-scroll value (0) and
        // strand the user at the top when the drawer closes.
        overlaySavedStateRef.current = {
            bodyOverflow: document.body.style.overflow,
            documentOverflow: document.documentElement.style.overflow,
            scrollY: window.scrollY,
        };

        // Scroll the parent back to the top before we measure the iframe
        // container. This makes `containerRect.top` resolve to the iframe's
        // natural inline offset (just below any sticky top chrome) rather
        // than a negative value that we'd clamp to 0 — the clamp races
        // with paint and can leave the in-iframe drawer's top edge cut
        // off when the user had scrolled past the iframe before opening.
        window.scrollTo(0, 0);

        // Capture how far the iframe sits below the viewport top right
        // now — this is the space occupied by any sticky chrome above
        // (lqa-app nav, tabs, breadcrumbs). The fixed iframe will be
        // anchored at that offset so the chrome stays visible. Negative
        // values (user scrolled past the iframe) clamp to 0.
        const containerRect = containerRef.current?.getBoundingClientRect();
        const topOffset = containerRect ? Math.max(0, containerRect.top) : 0;
        setOverlayTopOffset(topOffset);

        // Lock the underlying page so it can't scroll behind the
        // overlay iframe.
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';

        setIsOverlayActive(true);

        // Tell the iframe what its visible viewport now is so the
        // drawer can size its panel exactly. After `setIsOverlayActive`,
        // React re-renders with the fixed-position iframe pinned below
        // the chrome — at that point `window.innerHeight` inside the
        // iframe equals `window.innerHeight - topOffset`. We post the
        // value synchronously here so the iframe doesn't have to wait
        // for a relayout.
        const visibleHeight = Math.max(300, window.innerHeight - topOffset);
        // Wait one frame so the iframe has been re-styled before the
        // drawer reads dimensions on the next render.
        requestAnimationFrame(() => {
            iframeRef.current?.contentWindow?.postMessage({ type: 'viewport-height', height: visibleHeight }, iframeOrigin);
        });
    }, [iframeOrigin]);

    // Restore overflow on unmount in case the iframe was destroyed
    // (e.g. parent navigation) before posting the matching
    // `overlay-mode: false`.
    useEffect(() => () => exitOverlayMode(), [exitOverlayMode]);

    useEffect(() => {
        const handleMessage = (event) => {
            if (event.origin !== iframeOrigin) {
                return;
            }

            if (event.data && event.data.type === 'overlay-mode') {
                if (event.data.open === true) {
                    enterOverlayMode();
                } else if (event.data.open === false) {
                    exitOverlayMode();
                }
                return;
            }

            // Handle resize messages — suppressed while in overlay mode
            // so the iframe's own auto-grow ResizeObserver doesn't fight
            // the parent-driven viewport sizing.
            if (event.data && event.data.type === 'resize') {
                if (overlayActiveRef.current) {
                    return;
                }
                const newHeight = Math.max(event.data.height, minHeight); // Set minimum height
                setIframeHeight(newHeight);
            }

            if (event.data && event.data.type === 'scrollToTop') {
                window.scrollTo(0, 0);
            }

            if (event.data?.type === 'MIXPANEL_TRACK' && typeof event.data.event === 'string') {
                Analytics.sendEvent(event.data.event, event.data.properties);
            }

            if (event.data?.type === 'forbidden') {
                setIsForbidden(true);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [minHeight, iframeOrigin, enterOverlayMode, exitOverlayMode]);

    const { selectedBrandId, setBrandId, selectedHotelId, setHotelId, selectedBrand, selectedHotel } =
        useCustomerBrandHotelSelection();

    useEffect(() => {
        if (!requiredFlag) {
            return;
        }

        let cancelled = false;
        setIsForbidden(false);
        setAccessState('checking');

        fetchFeatureFlagsFromAPI()
            .then(() => {
                if (cancelled) {
                    return;
                }
                setAccessState(getFlag(requiredFlag) ? 'granted' : 'denied');
            })
            .catch(() => {
                if (cancelled) {
                    return;
                }
                setAccessState('denied');
            });

        return () => {
            cancelled = true;
        };
    }, [requiredFlag, selectedHotelId]);

    useEffect(() => {
        const handleRedirect = (event) => {
            if (event.origin !== iframeOrigin) {
                return;
            }

            // Handle redirect messages
            if (event.data && event.data.type === 'redirect') {
                setBrandId(event.data.brandId);
                setHotelId(event.data.hotelId);
                window.location.href = event.data.url;
            }
        };

        window.addEventListener('message', handleRedirect);

        return () => window.removeEventListener('message', handleRedirect);
    }, [iframeOrigin]);

    const iframeUrl = useMemo(() => {
        // Determine the URL based on the environment
        const enrichedUrl = new URL(url);

        if (userData) {
            enrichedUrl.searchParams.append('token', userData.token);
        }
        if (selectedBrandId) {
            enrichedUrl.searchParams.append('brandId', selectedBrandId.toString());
            enrichedUrl.searchParams.append('brandName', selectedBrand.name);
        }
        if (selectedHotelId) {
            enrichedUrl.searchParams.append('hotelId', selectedHotelId.toString());
            enrichedUrl.searchParams.append('hotelName', selectedHotel.name);
        }

        return enrichedUrl;
    }, [url, selectedBrandId, selectedHotelId, userData, selectedBrand, selectedHotel]);

    const iframeStyle: React.CSSProperties = isOverlayActive
        ? {
                ...BASE_OVERLAY_IFRAME_STYLE,
                top: overlayTopOffset,
                // Replaced elements (iframe) ignore `left:0; right:0` for
                // stretching, so explicit `width: 100%` + computed
                // `height` is required. `calc(100vh - <topOffset>)` makes
                // the iframe span from the nav offset to viewport bottom.
                height: `calc(100vh - ${overlayTopOffset}px)`,
            }
        : {
                width: '100%',
                height: `${iframeHeight}px`, // Explicit height based on content
                border: 'none',
                display: 'block',
            };

    return (
        <div
            ref={containerRef}
            className="my-lqa-container"
            style={{
                display: 'flex',
                flexDirection: 'column',
                flex: '1 1 auto',
                height: `100%`,
            }}
        >
            {isForbidden || accessState === 'denied' ? (
                <NonIdealState
                    title={t('error.Access denied')}
                    description={t("error.You don't have permission to view this page")}
                    intent="danger"
                />
            ) : accessState === 'checking' ? (
                <LoadingContent layout="absolute" />
            ) : (
                <iframe ref={iframeRef} src={iframeUrl.href} title="My LQA Application" style={iframeStyle} />
            )}
        </div>
    );
}

interface MicroAppProps {
    url: string;
    requiredFlag?: string;
}
